import type { StatTestType } from '../../dashboards/data-generator/test-generator/test-library-datasets.model';

// ---------------------------------------------------------------------------
// Test parameters
// ---------------------------------------------------------------------------

export type TailType = 'left' | 'two' | 'right';
export type SignificanceLevel = 0.01 | 0.05 | 0.1;

export const SIGNIFICANCE_LEVELS: readonly SignificanceLevel[] = [0.1, 0.05, 0.01];
export const SIGNIFICANCE_LABELS: Readonly<Record<string, string>> = {
  '0.1': '10%',
  '0.05': '5%',
  '0.01': '1%',
};

export interface TestParameters {
  readonly alpha: SignificanceLevel;
  readonly tailType: TailType;
  readonly nullMean: number;
}

export const DEFAULT_TEST_PARAMETERS: TestParameters = {
  alpha: 0.05,
  tailType: 'two',
  nullMean: 0,
};

// ---------------------------------------------------------------------------
// Test results
// ---------------------------------------------------------------------------

export type DistributionType = 't' | 'chi-squared' | 'binomial';

export interface TestResult {
  readonly testType: StatTestType;
  readonly distributionType: DistributionType;
  readonly testStatistic: number;
  readonly degreesOfFreedom: number;
  readonly pValue: number;
  readonly alpha: SignificanceLevel;
  readonly tailType: TailType;
  readonly rejectNull: boolean;
  /** 1 value for one-tailed, 2 for two-tailed (sorted ascending) */
  readonly criticalValues: readonly number[];
  /** Descriptive sample statistics for display, e.g. { 'x̄': 5.2, s: 1.3, n: 30 } */
  readonly sampleStats: Readonly<Record<string, number>>;
  /** Null hypothesis parameter (μ₀ for t-tests). Undefined for χ² and sign tests. */
  readonly nullMean?: number;
}

export interface TestResultError {
  readonly error: string;
}

export type TestResultOrError = TestResult | TestResultError;

export function isTestError(r: TestResultOrError): r is TestResultError {
  return 'error' in r;
}
